import React, { useState } from "react";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { LocalPostData } from "../../../models/LocalPostData";
import DataSvc from "../../../services/dataSvc";
import { createNestedFormData } from "../../../utils/formData";
import InputTextArea from "../../FormElement/InputTextarea";
import { MediaUpload } from "../../FormElement/MediaUpload";
import ModalConfirm from "../../ModalConfirm";

import "./styles.scss";

export interface IUpdateGbpLocalPostFormProps {
  localPost: LocalPostData;
  onClose?: () => void;
  onSuccess?: () => void;
}

export const UpdateGbpLocalPostForm: React.FunctionComponent<
  IUpdateGbpLocalPostFormProps
> = (props) => {
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(
      `analysisCollectionsPage.modalUpdateLocalPost.updateLocalPostForm.${key}`
    );

  const { localPost, onClose, onSuccess } = props;

  const [summary, setSummary] = useState<string>(localPost.summary || "");
  const [summaryError, setSummaryError] = useState<string | undefined>(
    undefined
  );
  const [media, setMedia] = useState<File[]>([]);
  const [mediaError, setMediaError] = useState<string | undefined>(undefined);
  const [shownModalConfirm, setShownModalConfirm] = useState<boolean>(false);

  const onChangeSummary = (value: string) => {
    setSummary(value);
    if (value.length === 0) {
      setSummaryError(t("summary_required"));
    } else if (value.length > 1500) {
      setSummaryError(t("summary_too_long"));
    } else {
      setSummaryError(undefined);
    }
  };

  const onChangeMedia = (files: File[]) => {
    setMedia(files);
  };

  const onChangeMediaError = (error: string | undefined) => {
    setMediaError(error);
  };

  const hasChanges = () => {
    return summary !== (localPost.summary || "") || media.length > 0;
  };

  const isFormValid = () => {
    return !summaryError && !mediaError && hasChanges();
  };

  const handleSubmit = async () => {
    try {
      const data: any = {};

      if (summary !== (localPost.summary || "")) {
        data.summary = summary;
      }

      const formData = createNestedFormData(data);

      if (media.length > 0) {
        media.forEach((_media) => {
          formData.append("media", _media);
        });
      }

      await DataSvc.updateLocalPost(
        localPost.location_id.toString(),
        localPost.id.toString(),
        formData
      );

      toast.success(t("update_success_message"));

      ReactGA.event({
        category: "update-local-post",
        action: "Update GBP Local Post",
      });

      if (onSuccess) {
        onSuccess();
      }

      setShownModalConfirm(false);
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error("Failed to update local post:", error);
    }
  };

  return (
    <div>
      <div className="update-gbp-local-post-form">
        <div className="media-upload-wrapper">
          <h3>{t("add_photo")}</h3>
          <MediaUpload
            files={media}
            changeFiles={onChangeMedia}
            error={mediaError}
            changeError={onChangeMediaError}
            maxFiles={1}
            maxSizeMiB={10}
            minWidth={400}
            minHeight={300}
          />
          {localPost.media && localPost.media.length > 0 && (
            <div className="current-media">
              <h4>{t("current_photo")}</h4>
              <div className="current-media-preview">
                <img src={localPost.media[0].googleUrl} alt="Current media" />
              </div>
            </div>
          )}
        </div>

        <div className="message-body">
          <h3>{t("summary")}</h3>
          <InputTextArea
            name="summary"
            value={summary}
            placeholder={t("summary_placeholder")}
            classNameContainer={"message-body-input margin-bottom-30"}
            errorLabel={summaryError}
            onChange={onChangeSummary}
            maxChars={1500}
            showChars={true}
          />
        </div>
      </div>

      <div className="update-gbp-local-post-warning">
        <span>{t("update_warning")}</span>
      </div>

      <div className="update-gbp-local-post-bottom-btns bottom-btns">
        <div>
          {onClose && (
            <button className="btn btn-border" onClick={onClose}>
              {t("cancel")}
            </button>
          )}
        </div>
        <div>
          <button
            className={`btn btn-blue ${isFormValid() ? "" : "disabled"}`}
            onClick={() => {
              if (isFormValid()) {
                setShownModalConfirm(true);
              }
            }}
            title={!hasChanges() ? t("no_changes_made") : ""}
          >
            {t("update")}
          </button>
        </div>
      </div>

      {shownModalConfirm && (
        <ModalConfirm
          title={_t("please_confirm_to_update_local_post")}
          cancelLabel={_t("cancel")}
          confirmLabel={_t("update")}
          onClose={() => {
            setShownModalConfirm(false);
          }}
          onConfirm={handleSubmit}
        >
          <div className="confirm-update-message">
            {t("confirm_update_message")}
          </div>
        </ModalConfirm>
      )}
    </div>
  );
};

export default UpdateGbpLocalPostForm;
